import * as XLSX from "xlsx";
import { calculateAge, convertCSVToJson } from "./utils";

export const candidateImportEditableFields = [
    { field: "name", label: "Name" },
    { field: "mobile", label: "Mobile" },
    { field: "gender", label: "Gender" },
    { field: "dob", label: "DOB" },
    { field: "height", label: "Height" },
    { field: "qualification", label: "Qualification" },
    { field: "communities", label: "Community / Caste" },
    { field: "father_name", label: "Father Name" },
    { field: "mother_name", label: "Mother Name" },
    { field: "number_of_brother", label: "Brothers" },
    { field: "number_of_sister", label: "Sisters" },
    { field: "marital_status", label: "Marital Status" },
    { field: "religion", label: "Religion" },
    { field: "address_of_residence", label: "Address", multiline: true },
];

const candidateImportPayloadFields = [
    "name",
    "email",
    "mobile",
    "gender",
    "dob",
    "tob",
    "birth_place",
    "height",
    "weight",
    "colour",
    "native_place",
    "religion",
    "mother_tongue",
    "communities",
    "hobbies",
    "father_name",
    "father_gotra",
    "mother_name",
    "mother_gotra",
    "number_of_brother",
    "number_of_sister",
    "manglic",
    "matching_horoscope",
    "qualification",
    "employeed",
    "business_company_name",
    "address_of_business",
    "address_of_residence",
    "annual_income",
    "financial_budget",
    "marital_status",
    "reffered_by",
    "city",
    "state",
    "zipcode",
    "show_to_everyone",
    "show_limited",
    "status",
    "age",
    "sanstha_id",
];

const cleanText = (value = "") =>
    String(value ?? "")
        .replace(/\u00a0/g, " ")
        .replace(/[ \t]+/g, " ")
        .trim();

const normalizeKey = (value = "") =>
    cleanText(value)
        .toLowerCase()
        .replace(/[^a-z0-9 ]+/g, " ")
        .replace(/\s+/g, "_");

const normalizeDateValue = (value = "") => {
    const normalized = cleanText(value);
    if (!normalized) {
        return "";
    }

    const ddMmYyyyMatch = normalized.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
    if (ddMmYyyyMatch) {
        const [, day, month, year] = ddMmYyyyMatch;
        const fullYear = year.length === 2 ? `20${year}` : year;
        return `${fullYear}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }

    const parsed = new Date(normalized);
    if (Number.isNaN(parsed.getTime())) {
        return normalized;
    }

    return parsed.toISOString().slice(0, 10);
};

const normalizeGender = (value = "") => {
    const normalized = cleanText(value).toLowerCase();
    if (!normalized) {
        return "";
    }
    if (normalized.startsWith("m")) {
        return "male";
    }
    if (normalized.startsWith("f")) {
        return "female";
    }
    return normalized;
};

const extractPhoneNumber = (value = "") => {
    const matches = cleanText(value).match(/\d{10,}/g);
    return matches && matches.length > 0 ? matches[0] : cleanText(value);
};

const normalizeRowKeys = (row = {}) =>
    Object.entries(row).reduce((acc, [key, value]) => {
        acc[normalizeKey(key)] = typeof value === "string" ? cleanText(value) : value;
        return acc;
    }, {});

export const normalizeCandidateDraft = (row = {}) => {
    const normalizedKeys = normalizeRowKeys(row);
    const normalized = {
        ...normalizedKeys,
        name: cleanText(normalizedKeys.name),
        email: cleanText(normalizedKeys.email),
        mobile: extractPhoneNumber(normalizedKeys.mobile || normalizedKeys.contact),
        gender: normalizeGender(normalizedKeys.gender),
        dob: normalizeDateValue(normalizedKeys.dob),
        height: cleanText(normalizedKeys.height),
        qualification: cleanText(normalizedKeys.qualification || normalizedKeys.education),
        communities: cleanText(normalizedKeys.communities || normalizedKeys.caste),
        father_name: cleanText(normalizedKeys.father_name),
        mother_name: cleanText(normalizedKeys.mother_name),
        number_of_brother: cleanText(normalizedKeys.number_of_brother || normalizedKeys.brother || normalizedKeys.brothers),
        number_of_sister: cleanText(normalizedKeys.number_of_sister || normalizedKeys.sister || normalizedKeys.sisters),
        marital_status: cleanText(normalizedKeys.marital_status || normalizedKeys.marital || normalizedKeys.maternal),
        religion: cleanText(normalizedKeys.religion),
        address_of_residence: cleanText(normalizedKeys.address_of_residence || normalizedKeys.address),
        _meta: row._meta || normalizedKeys._meta,
    };

    if (normalized.dob) {
        const dob = new Date(normalized.dob);
        if (!Number.isNaN(dob.getTime())) {
            normalized.age = calculateAge(dob);
        }
    }

    return normalized;
};

const readFileAsText = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result || "");
        reader.onerror = () => reject(new Error("Unable to read file."));
        reader.readAsText(file);
    });

const readFileAsArrayBuffer = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = () => reject(new Error("Unable to read file."));
        reader.readAsArrayBuffer(file);
    });

export const parseSpreadsheetFile = async (file) => {
    const extension = file.name.split(".").pop()?.toLowerCase();

    if (extension === "csv") {
        const text = await readFileAsText(file);
        return convertCSVToJson(text)
            .filter((row) => Object.values(row).some((value) => cleanText(value)))
            .map((row) => normalizeCandidateDraft(row));
    }

    if (extension === "xlsx" || extension === "xls") {
        const buffer = await readFileAsArrayBuffer(file);
        const workbook = XLSX.read(buffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "", raw: false });
        return rows
            .filter((row) => Object.values(row).some((value) => cleanText(value)))
            .map((row) => normalizeCandidateDraft(row));
    }

    throw new Error("Unsupported spreadsheet format.");
};

export const buildCandidateImportPayload = (rows = [], sansthaId, showToEveryone = true) =>
    rows.map((row) => {
        const normalized = normalizeCandidateDraft(row);
        const payload = candidateImportPayloadFields.reduce((acc, field) => {
            if (normalized[field] !== undefined) {
                acc[field] = normalized[field];
            }
            return acc;
        }, {});

        payload.sanstha_id = sansthaId;
        payload.show_to_everyone = showToEveryone;
        payload.age = normalized.age || "";

        return payload;
    });
